/** MI DOMINO — 🏅 Logros (achievements).
 *  Permanent badges derived from the player's lifetime STATS (unlike the daily
 *  missions, which are deltas). Deterministic: a badge is "earned" the moment
 *  its stat crosses the bar, so nothing in the game loop had to change — every
 *  saveStats() calls achTick(), which pays the PhilCoins once and remembers the
 *  id in STATS.ach (the server unions that list across devices). */
import { STATS } from '../state';
import { LANG } from '../ui/i18n';
import { toast } from '../ui/toast';
import { earnCoins } from '../game/engine';
import { RANKS } from './ranks';

export interface Achievement {
  id: string; icon: string; es: string; en: string; coins: number;
  /** stat key + bar; the dashboard shows progress cur/n */
  stat: string; n: number;
  /** flavour line */
  des: string; den: string;
}

export const ACHIEVEMENTS: Achievement[] = [
  { id: 'hand1', icon: '✋', es: 'Primera mano', en: 'First hand', coins: 10, stat: 'hands', n: 1, des: 'Jugaste tu primera mano.', den: 'You played your first hand.' },
  { id: 'hands50', icon: '🖐️', es: 'Medio centenar', en: 'Half a hundred', coins: 50, stat: 'hands', n: 50, des: '50 manos en la mesa.', den: '50 hands at the table.' },
  { id: 'hands250', icon: '💪', es: 'Veterano', en: 'Veteran', coins: 150, stat: 'hands', n: 250, des: '250 manos jugadas.', den: '250 hands played.' },
  { id: 'win1', icon: '🏆', es: 'Primera victoria', en: 'First win', coins: 50, stat: 'matchesWon', n: 1, des: 'Ganaste tu primera partida.', den: 'You won your first match.' },
  { id: 'win10', icon: '🥇', es: 'Diez de diez', en: 'Ten wins', coins: 120, stat: 'matchesWon', n: 10, des: '10 partidas ganadas.', den: '10 matches won.' },
  { id: 'win50', icon: '👑', es: 'El Jefe de la mesa', en: 'Boss of the table', coins: 400, stat: 'matchesWon', n: 50, des: '50 partidas ganadas.', den: '50 matches won.' },
  { id: 'capicu1', icon: '🎯', es: '¡Capicú!', en: 'Capicú!', coins: 40, stat: 'capicus', n: 1, des: 'Tu primer capicú.', den: 'Your first capicú.' },
  { id: 'capicu10', icon: '🎯', es: 'Francotirador', en: 'Sharpshooter', coins: 150, stat: 'capicus', n: 10, des: '10 capicús.', den: '10 capicús.' },
  { id: 'chuchazo1', icon: '💥', es: '¡Chuchazo!', en: 'Chuchazo!', coins: 100, stat: 'chuchazos', n: 1, des: 'Cerraste con el doble blanco.', den: 'You went out with the double blank.' },
  { id: 'tranque5', icon: '🔒', es: 'Cerrajero', en: 'Locksmith', coins: 80, stat: 'tranquesWon', n: 5, des: '5 tranques ganados.', den: '5 tranques won.' },
  { id: 'streak3', icon: '🔥', es: 'Racha caliente', en: 'Hot streak', coins: 100, stat: 'bestStreak', n: 3, des: '3 victorias seguidas.', den: '3 wins in a row.' },
  { id: 'streak6', icon: '🌋', es: 'Imparable', en: 'Unstoppable', coins: 300, stat: 'bestStreak', n: 6, des: '6 victorias seguidas.', den: '6 wins in a row.' },
  { id: 'online1', icon: '🌐', es: 'Mesa global', en: 'Global table', coins: 50, stat: 'onlineHands', n: 1, des: 'Tu primera mano online.', den: 'Your first online hand.' },
  { id: 'online50', icon: '🛰️', es: 'Ciudadano del mundo', en: 'World citizen', coins: 200, stat: 'onlineHands', n: 50, des: '50 manos online.', den: '50 online hands.' },
  { id: 'rank5', icon: RANKS[4][0], es: RANKS[4][1], en: RANKS[4][2], coins: 200, stat: 'xp', n: RANKS[4][3], des: 'Llegaste al rango 5.', den: 'You reached rank 5.' },
  { id: 'rank10', icon: RANKS[9][0], es: RANKS[9][1], en: RANKS[9][2], coins: 1000, stat: 'xp', n: RANKS[9][3], des: 'La cima: King Kong.', den: 'The top: King Kong.' },
];

const statOf = (k: string): number => Number((STATS as any)[k] || 0);
const unlocked = (): string[] => Array.isArray(STATS.ach) ? STATS.ach : [];

export interface AchView extends Achievement { cur: number; done: boolean; }
export function achList(): AchView[] {
  const have = new Set(unlocked());
  return ACHIEVEMENTS.map((a) => {
    const cur = Math.min(a.n, statOf(a.stat));
    return { ...a, cur, done: have.has(a.id) || cur >= a.n };
  });
}

/** Called after every stats save: pays out whatever was just earned (once). */
export function achTick(): void {
  const have = new Set(unlocked());
  let changed = false;
  for (const a of ACHIEVEMENTS) {
    if (have.has(a.id) || statOf(a.stat) < a.n) continue;
    have.add(a.id);
    changed = true;
    const label = LANG === 'en' ? a.en : a.es;
    earnCoins(a.coins, (LANG === 'en' ? 'achievement: ' : 'logro: ') + label + ' ' + a.icon);
    try { toast(`🏅 ${LANG === 'en' ? 'Achievement' : 'Logro'}: ${a.icon} ${label}  +${a.coins} 🪙`); } catch { /* */ }
  }
  if (!changed) return;
  STATS.ach = [...have];
  // persist without re-entering saveStats (the coin save above already syncs the cloud)
  try { localStorage.setItem('stats', JSON.stringify(STATS)); } catch { /* */ }
}

/** Dashboard card: a badge grid, locked ones dimmed with their progress. */
export function achievementsHTML(): string {
  const en = LANG === 'en';
  const list = achList();
  const doneN = list.filter((a) => a.done).length;
  const cells = list.map((a) => `<div class="ach${a.done ? ' done' : ''}" title="${en ? a.den : a.des}">
      <span class="aicon">${a.icon}</span>
      <b>${en ? a.en : a.es}</b>
      <small>${a.done ? `+${a.coins} 🪙 ✓` : `${a.cur}/${a.n}`}</small>
    </div>`).join('');
  return `<div class="quests">
    <div class="qhead"><b>🏅 ${en ? 'Achievements' : 'Logros'}</b><small>${doneN}/${list.length}</small></div>
    <div class="ach-grid">${cells}</div>
  </div>`;
}
